import React, { useState, useEffect, useMemo } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import axios from 'axios';
import {
  Search,
  ChevronDown,
  Plus,
  Lock,
  PanelLeft,
  X,
  Sparkles,
  BookOpen,
  ShieldAlert,
  Target,
  ChevronRight
} from 'lucide-react';
import { motion, AnimatePresence } from 'framer-motion';
import { handleAvatarError, DEFAULT_AVATAR } from '../../../utils/avatarHelper';

const Sidebar = ({ curriculum = [], currentCourse = "" }) => {
  const navigate = useNavigate();
  const [isCoursesOpen, setIsCoursesOpen] = useState(true);
  const [isMobileOpen, setIsMobileOpen] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [suggestions, setSuggestions] = useState([]);
  const [isSearching, setIsSearching] = useState(false);
  const [history, setHistory] = useState([]);
  const [showAllHistory, setShowAllHistory] = useState(false);

  // Calculate mastery progress
  const masteryProgress = useMemo(() => {
    if (!curriculum.length) return 0;
    const coveredCount = curriculum.filter(t => t.covered).length;
    return Math.round((coveredCount / curriculum.length) * 100);
  }, [curriculum]);

  const displayTopics = useMemo(() => {
    if (curriculum.length > 0) {
        return curriculum.map((topic, index) => {
            const isPreviousCovered = index === 0 || curriculum[index-1].covered;
            const locked = !(topic.covered || isPreviousCovered);
            return {
                id: topic.id,
                title: topic.title,
                type: topic.type || 'topic',
                locked: locked,
                covered: !!topic.covered,
                content: topic.content
            };
        });
    }
    // Fallback static topics
    return [
        { title: 'Path Discovery', locked: false },
        { title: 'Curriculum Generation', locked: true },
    ];
  }, [curriculum]);

  useEffect(() => {
    const delayDebounceFn = setTimeout(async () => {
      if (searchQuery.length > 2) {
        setIsSearching(true);
        try {
          const res = await axios.post(`${process.env.REACT_APP_BASE_URL || ''}/api/ai/course-suggestions`, { query: searchQuery });
          if (res.data?.success) {
            setSuggestions(res.data.suggestions);
          }
        } catch (err) {
          console.error("Suggestions error:", err);
        } finally {
          setIsSearching(false);
        }
      } else {
        setSuggestions([]);
      }
    }, 500);

    return () => clearTimeout(delayDebounceFn);
  }, [searchQuery]);

  // Fetch course history
  useEffect(() => {
    const fetchHistory = async () => {
      const student = JSON.parse(localStorage.getItem('student'));
      if (!student?.id) return;

      try {
        const res = await axios.get(`${process.env.REACT_APP_BASE_URL || ''}/api/ai/history/${student.id}`);
        if (res.data?.success) {
          setHistory(res.data.data);
        }
      } catch (err) {
        console.error("History fetch error:", err);
      }
    };
    fetchHistory();
  }, [currentCourse]); // Refresh history if course changes

  const visibleHistory = useMemo(() => {
    if (showAllHistory) return history;
    return history.slice(0, 3);
  }, [history, showAllHistory]);

  return (
    <>
      {/* MOBILE TOGGLE BUTTON */}
      <button
        onClick={() => setIsMobileOpen(true)}
        className="md:hidden mt-20 fixed top-4 left-4 z-50 bg-white p-2 rounded-lg shadow border"
      >
        <PanelLeft size={20} />
      </button>

      {/* MOBILE OVERLAY */}
      {isMobileOpen && (
        <div
          className="fixed inset-0 bg-black/40 z-40 md:hidden"
          onClick={() => setIsMobileOpen(false)}
        />
      )}

      {/* SIDEBAR */}
      <aside
        className={`
          fixed md:static top-0 left-0 z-50
          h-screen w-72 md:w-80
          glass-panel border-r border-white/20
          flex flex-col p-6 font-sans
          transition-transform duration-300
          ${isMobileOpen ? 'translate-x-0' : '-translate-x-full'}
          md:translate-x-0 shadow-2xl
        `}
      >
        {/* HEADER */}
        <div className="flex items-center justify-between mb-8">
          <div className="flex items-center gap-3">
            <div className="w-11 h-11 bg-blue-600 rounded-2xl flex items-center justify-center shadow-lg shadow-blue-200">
               <Sparkles className="text-white" size={20} />
            </div>
            <div>
              <h1 className="text-xl font-black text-gray-900 tracking-tighter italic">
                PEERMATE
              </h1>
              <p className="text-[10px] text-blue-500 font-black uppercase tracking-widest">
                Mastery Engine
              </p>
            </div>
          </div>

          {/* CLOSE ON MOBILE */}
          <button
            onClick={() => setIsMobileOpen(false)}
            className="md:hidden p-2 hover:bg-white/50 rounded-xl"
          >
            <X size={20} className="text-gray-500" />
          </button>
        </div>

        {/* SEARCH - Minimal Glass */}
        <div className="relative mb-8">
          <Search
            className="absolute left-4 top-1/2 -translate-y-1/2 text-gray-400"
            size={16}
          />
          <input
            type="text"
            placeholder="Explore topics..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            className="w-full bg-white/50 border border-white/30 rounded-2xl py-3 pl-12 pr-4 text-xs font-bold focus:ring-2 focus:ring-blue-400 outline-none transition-all"
          />
          
          {/* SEARCH SUGGESTIONS DROP DOWN */}
          <AnimatePresence>
            {(suggestions.length > 0 || isSearching) && (
              <motion.div 
                initial={{ opacity: 0, y: -10 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0, y: -10 }}
                className="absolute top-full left-0 right-0 mt-2 bg-white/90 backdrop-blur-md border border-white/50 rounded-2xl shadow-xl z-[60] overflow-hidden"
              >
                {isSearching ? (
                  <div className="p-4 text-center">
                     <div className="w-5 h-5 border-2 border-blue-500 border-t-transparent rounded-full animate-spin mx-auto" />
                  </div>
                ) : (
                  <div className="py-2">
                    {suggestions.map((s, i) => (
                      <div 
                        key={i}
                        onClick={() => navigate('/peerchat', { state: { course: s } })}
                        className="px-4 py-3 hover:bg-blue-50 text-xs font-bold text-gray-700 cursor-pointer flex items-center gap-2 transition-colors border-b border-gray-50 last:border-0"
                      >
                        <Sparkles size={12} className="text-blue-500" />
                        {s}
                      </div>
                    ))}
                  </div>
                )}
              </motion.div>
            )}
          </AnimatePresence>
        </div>

        {/* PROGRESS CARD */}
        <div className="bg-white/40 border border-white/60 rounded-3xl p-5 mb-8 shadow-sm">
            <div className="flex justify-between items-center mb-3">
                <span className="text-[11px] font-black uppercase text-gray-400">Mastery Progress</span>
                <span className="text-xs font-black text-blue-600">{masteryProgress}%</span>
            </div>
            <div className="w-full h-1.5 bg-white/50 rounded-full overflow-hidden">
                <div className="h-full bg-blue-600 rounded-full transition-all duration-1000" style={{ width: `${masteryProgress}%` }} />
            </div>
        </div>

        {/* COURSES SECTION */}
        <div className="mb-8">
          <button
            onClick={() => setIsCoursesOpen(!isCoursesOpen)}
            className="flex items-center justify-between w-full text-[11px] font-black text-gray-400 uppercase tracking-widest mb-4 group"
          >
            <span>Active Courses</span>
            <ChevronDown
              size={14}
              className={`transition-transform duration-300 ${
                isCoursesOpen ? 'rotate-0' : '-rotate-90'
              } group-hover:text-blue-600`}
            />
          </button>

          <AnimatePresence>
            {isCoursesOpen && (
                <div className="space-y-3 pl-2">
                    {visibleHistory.length > 0 ? (
                        <>
                          {visibleHistory.map((item, idx) => {
                            const isActive = item.course_name === currentCourse;
                            const progress = Math.round((item.covered_topics / item.total_topics) * 100);
                            
                            return (
                              <motion.div 
                                key={idx}
                                initial={{ opacity: 0, x: -10 }}
                                animate={{ opacity: 1, x: 0 }}
                                transition={{ delay: idx * 0.05 }}
                                onClick={() => navigate('/peerchat', { state: { course: item.course_name } })}
                                className={`group flex flex-col gap-2 p-3 rounded-2xl transition-all cursor-pointer border
                                  ${isActive 
                                    ? 'bg-blue-600 text-white border-blue-400 shadow-lg shadow-blue-100' 
                                    : 'bg-white/40 text-gray-700 border-white/60 hover:bg-white hover:border-blue-200'
                                  }
                                `}
                              >
                                <div className="flex items-center gap-3">
                                  <div className={`w-8 h-8 rounded-xl flex items-center justify-center shrink-0
                                    ${isActive ? 'bg-white/20' : 'bg-blue-50 text-blue-600'}
                                  `}>
                                      <BookOpen size={14} />
                                  </div>
                                  <div className="flex flex-col overflow-hidden">
                                     <span className="text-[10px] font-black uppercase tracking-tighter truncate">
                                      {item.course_name}
                                     </span>
                                     <span className={`text-[8px] font-bold ${isActive ? 'text-white/60' : 'text-gray-400'}`}>
                                      {item.covered_topics}/{item.total_topics} Topics Mastered
                                     </span>
                                  </div>
                                </div>
                                
                                {/* Mini Progress Bar */}
                                <div className={`w-full h-1 rounded-full overflow-hidden ${isActive ? 'bg-white/20' : 'bg-gray-100'}`}>
                                   <div 
                                    className={`h-full rounded-full transition-all duration-1000 ${isActive ? 'bg-white' : 'bg-blue-500'}`} 
                                    style={{ width: `${progress}%` }} 
                                   />
                                </div>
                              </motion.div>
                            );
                          })}
                          
                          {history.length > 3 && (
                            <button 
                              onClick={() => setShowAllHistory(!showAllHistory)}
                              className="w-full py-2 text-[10px] font-black text-blue-500 uppercase tracking-widest hover:text-blue-700 transition-colors flex items-center justify-center gap-1"
                            >
                              {showAllHistory ? 'See Less' : `See ${history.length - 3} More Courses`}
                              <ChevronDown size={12} className={`transition-transform ${showAllHistory ? 'rotate-180' : ''}`} />
                            </button>
                          )}
                        </>
                    ) : (
                        currentCourse ? (
                             <div className="flex items-center gap-3 p-3 rounded-2xl bg-blue-600 text-white shadow-lg">
                                <div className="w-8 h-8 rounded-xl bg-white/20 flex items-center justify-center">
                                    <Sparkles size={14} />
                                </div>
                                <span className="text-[10px] font-black uppercase tracking-tighter truncate">{currentCourse}</span>
                            </div>
                        ) : (
                             <div className="flex items-center gap-3 p-3 rounded-2xl border border-dashed border-gray-200 text-gray-400">
                                <span className="text-[10px] font-black uppercase tracking-tighter">No Active Path</span>
                            </div>
                        )
                    )}
                </div>
            )}
          </AnimatePresence>

          <Link to="/Peermatehomepage" className="block w-full">
            <button className="mt-6 flex items-center gap-2 text-[11px] font-black text-blue-600 uppercase tracking-widest hover:translate-x-1 transition-all">
                <Plus size={14} /> Start New Path
            </button>
          </Link>
        </div>

        {/* QUICK ACCESS TOPICS */}
        <div className="flex-1 overflow-y-auto pr-2 custom-scrollbar">
          <h3 className="text-[11px] font-black text-gray-400 uppercase tracking-widest mb-4">
            Curriculum Breakdown
          </h3>

          <div className="space-y-4">
            {displayTopics.map((topic, index) => (
              <div
                key={index}
                className={`flex items-center justify-between p-3 rounded-2xl border transition-all cursor-pointer group
                    ${topic.locked ? 'bg-gray-50/30 border-transparent opacity-60' : 'bg-white/50 border-white/40 hover:bg-white'}
                `}
              >
                <div className="flex items-center gap-3 overflow-hidden">
                    <div className={`shrink-0 w-8 h-8 rounded-lg flex items-center justify-center text-[10px] font-black
                      ${topic.covered 
                        ? 'bg-emerald-100 text-emerald-600' 
                        : topic.locked 
                          ? 'bg-gray-100 text-gray-400' 
                          : topic.type === 'exam'
                            ? 'bg-purple-100 text-purple-600 animate-pulse'
                            : topic.type === 'test'
                              ? 'bg-orange-100 text-orange-600 animate-pulse'
                              : 'bg-blue-100 text-blue-600 animate-pulse'
                      }`}
                    >
                      {topic.type === 'exam' ? <ShieldAlert size={14} /> : topic.type === 'test' ? <Target size={14} /> : index + 1}
                    </div>

                    <div className="flex flex-col overflow-hidden">
                        <span className={`text-[8px] uppercase tracking-tighter font-black italic
                          ${topic.type === 'exam' ? 'text-purple-500' : topic.type === 'test' ? 'text-orange-500' : 'text-blue-500'}`}
                        >
                          {topic.type || 'TOPIC'} {index + 1}
                        </span>
                        <span className={`text-[11px] font-extrabold truncate ${topic.locked ? 'text-gray-400' : 'text-gray-700'}`}>
                        {topic.title}
                        </span>
                    </div>
                </div>

                {topic.locked ? (
                  <Lock size={10} className="text-gray-300 shrink-0" />
                ) : topic.covered ? (
                  <Sparkles size={10} className="text-emerald-500 shrink-0" />
                ) : (
                  <ChevronRight size={10} className="text-blue-400 group-hover:translate-x-1 transition-transform" />
                )}
              </div>
            ))}
          </div>
        </div>

        {/* FOOTER */}
        <div className="mt-8 pt-6 border-t border-white/20">
            <div className="flex items-center gap-3 p-3 rounded-2xl bg-gray-900 text-white shadow-xl">
                <div className="w-10 h-10 rounded-xl overflow-hidden bg-blue-500">
                    <img 
                      src="https://api.dicebear.com/7.x/avataaars/svg?seed=Felix" 
                      alt="User" 
                      onError={handleAvatarError}
                    />
                </div>
                <div>
                    <p className="text-[11px] font-black uppercase tracking-tight">Active Scholar</p>
                    <p className="text-[10px] text-gray-400 font-bold">Standard Tier</p>
                </div>
            </div>
        </div>
      </aside>
    </>
  );
};

export default Sidebar;
